
import Vue from 'vue';
import _isNil from 'lodash/isNil';
import BaseSlider from '@/components/base/BaseSlider.vue';
import RunnitDynamicFieldLabelRow from '@/views/Runnits/RunnitSettings/RunnitDynamicFieldLabelRow.vue';
import { RunnitDynamicFieldMixin } from '@/views/Runnits/RunnitSettings/mixins/RunnitDynamicFieldMixin';
import { getLogicOnFieldsFromField } from '@run-diffusion/shared';
import { findClosestSliderValue } from '@/utils/closestOptionUtils';
import SliderSelect from '@/components/base/SliderSelect.vue';
import { mapActions } from 'vuex';
import { SNACKBAR_STATUS } from '@/constants/constants';

export default Vue.extend({
	name: 'UpscaleFactorSlider',
	mixins: [
		RunnitDynamicFieldMixin,
	],
	props: {
		field: { type: Object, required: true },
		value: { type: Number, default: null },
		adminOnly: { type: Boolean, default: false },
		inputValues: { type: Object, required: true },
	},
	data () {
		return {};
	},
	computed: {
		hasOptions () {
			return this.field.display.options && this.field.display.options.length > 0;
		},
		sliderOptions () {
			if (!this.hasOptions) return [];
			return this.field.display.options;
		},
		logicOnFields () {
			return getLogicOnFieldsFromField(this.field, this.inputValues);
		},
		autoSetValue () {
			return this.logicOnFields.autoSetValue;
		},
		autoSetError () {
			return this.logicOnFields.autoSetError;
		},
		dataInputValue () {
			// Get the input value from the fieldDefUuid specified in field.display.dataFieldDefUuid
			const dataFieldDefUuid = this._get(this.field, 'display.dataFieldDefUuid');
			if (dataFieldDefUuid) {
				return this.inputValues[dataFieldDefUuid];
			}
			return null;
		},
		inputWidth () {
			if (!this.dataInputValue) return null;

			let width: number = 0;

			// Handle image data
			if (this.dataInputValue.image?.nodeRunResult) {
				width = this.dataInputValue.image.nodeRunResult.file.width;
			} else if (this.dataInputValue.nodeRunResult) {
				width = this.dataInputValue.nodeRunResult.file.width;
			} else if (this.dataInputValue.image?.upload) {
				width = this.dataInputValue.image.upload.width;
			} else if (this.dataInputValue.upload) {
				width = this.dataInputValue.upload.width;
			}

			// Handle video data
			if (this.dataInputValue.video?.nodeRunResult) {
				width = this.dataInputValue.video.nodeRunResult.file.width;
			} else if (this.dataInputValue.video?.upload) {
				width = this.dataInputValue.video.upload.width;
			}

			return width > 0 ? width : null;
		},
		inputHeight () {
			if (!this.dataInputValue) return null;

			let height: number = 0;

			// Handle image data
			if (this.dataInputValue.image?.nodeRunResult) {
				height = this.dataInputValue.image.nodeRunResult.file.height;
			} else if (this.dataInputValue.nodeRunResult) {
				height = this.dataInputValue.nodeRunResult.file.height;
			} else if (this.dataInputValue.image?.upload) {
				height = this.dataInputValue.image.upload.height;
			} else if (this.dataInputValue.upload) {
				height = this.dataInputValue.upload.height;
			}

			// Handle video data
			if (this.dataInputValue.video?.nodeRunResult) {
				height = this.dataInputValue.video.nodeRunResult.file.height;
			} else if (this.dataInputValue.video?.upload) {
				height = this.dataInputValue.video.upload.height;
			}

			return height > 0 ? height : null;
		},
		maxWidth () {
			return this.field.display.maxWidth || 3840;
		},
		maxHeight () {
			return this.field.display.maxHeight || 2160;
		},
		finalWidth () {
			if (!this.inputWidth || !this.value) return null;
			return Math.round(this.inputWidth * this.value);
		},
		finalHeight () {
			if (!this.inputHeight || !this.value) return null;
			return Math.round(this.inputHeight * this.value);
		},
		exceedsMaxDimension () {
			return (this.finalWidth && this.finalWidth > this.maxWidth) ||
				(this.finalHeight && this.finalHeight > this.maxHeight);
		},
		finalResolution () {
			if (!this.finalWidth || !this.finalHeight) return null;
			return `${this.finalWidth}x${this.finalHeight}`;
		},
		upscaleFactorRules () {
			return [
				(v: number) => {
					if (this.exceedsMaxDimension) {
						const widthExceeded = this.finalWidth && this.finalWidth > this.maxWidth;
						const heightExceeded = this.finalHeight && this.finalHeight > this.maxHeight;

						if (widthExceeded && heightExceeded) {
							return `Final dimensions (${this.finalWidth}x${this.finalHeight}) exceed maximum allowed (${this.maxWidth}x${this.maxHeight})`;
						} else if (widthExceeded) {
							return `Final width (${this.finalWidth}px) exceeds maximum allowed width of ${this.maxWidth}px`;
						} else if (heightExceeded) {
							return `Final height (${this.finalHeight}px) exceeds maximum allowed height of ${this.maxHeight}px`;
						}
					}
					return true;
				},
			];
		},
	},
	watch: {
		autoSetValue: {
			handler (newValue: any, oldValue: any) {
				// If autoSet value changes, update the field value
				if (!_isNil(newValue) && newValue !== oldValue) {
					this.onFieldInput(newValue);
				}
			},
			immediate: true,
		},
		autoSetError: {
			handler (newVal: string, oldVal: string) {
				if (newVal && newVal !== oldVal) {
					this.updateSnackbar({
						status: SNACKBAR_STATUS.ERROR,
						message: newVal,
						show: true,
					});
				}
			},
			immediate: true,
		},
	},
	methods: {
		...mapActions([
			'updateSnackbar',
		]),
		_isNil,
		onFieldInput (val: any) {
			let valueToTest = null;
			// If field has autoSet value and editing is not allowed, use autoSet value
			if (_isNil(this.logicOnFields.autoSetValue) || this.logicOnFields.autoSetAllowEdit) {
				// Use the user's input value (either manual input or autoSet with allowEdit)
				valueToTest = val;
			} else {
				valueToTest = this.logicOnFields.autoSetValue;
			}

			// For SliderSelect (discrete options), find the closest value from available options
			if (this.hasOptions) {
				const optionValues = this.sliderOptions.map(option => option.value);
				const closestOptionValue = optionValues.reduce((closest, optionValue) => {
					const currentDistance = Math.abs(valueToTest - optionValue);
					const closestDistance = Math.abs(valueToTest - closest);
					return currentDistance < closestDistance ? optionValue : closest;
				});
				this.$emit('input', closestOptionValue);
				return;
			}

			// For BaseSlider (continuous), find the closest valid value based on field constraints
			const closestValue = findClosestSliderValue(
				valueToTest,
				_isNil(this.field.display.min) ? 1 : this.field.display.min,
				this.field.display.max || 4,
				this.field.display.step || 0.1
			);
			this.$emit('input', closestValue);
		},
	},
	components: {
		BaseSlider,
		RunnitDynamicFieldLabelRow,
		SliderSelect,
	},
});
